import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './BookingDashboard.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { appointmentService, authService, therapyPackageService } from '../../services';
import { getExpertImage } from '../../utils/expertImages';
import SafeImage from '../../components/SafeImage';

export default function BookingDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // BookingDashboard specific state - unique naming to avoid conflicts
  const [bookingDashboardSelectedDate, setBookingDashboardSelectedDate] = useState('Hôm nay');
  const [bookingDashboardSelectedTimeSlot, setBookingDashboardSelectedTimeSlot] = useState('');
  const [bookingDashboardExpertInfo, setBookingDashboardExpertInfo] = useState(null);
  const [bookingDashboardSelectedCalendarDate, setBookingDashboardSelectedCalendarDate] = useState(null);
  const [bookingDashboardOrderNumber] = useState(1);
  // Khởi tạo lịch cho tháng/năm hiện tại
  const today = new Date();
  const [bookingDashboardCurrentMonth, setBookingDashboardCurrentMonth] = useState(today.getMonth() + 1); // tháng bắt đầu từ 1
  const [bookingDashboardCurrentYear, setBookingDashboardCurrentYear] = useState(today.getFullYear());
  const [bookingDashboardShowYearPicker, setBookingDashboardShowYearPicker] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isCreatingAppointment] = useState(false);
  const [therapyPackages, setTherapyPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [inlineMessage, setInlineMessage] = useState(null); // { type: 'error'|'success', text: string }
  // lấy dữ liệu UI nhỏ từ chuyên gia đã chọn hoặc trạng thái hiện tại
  const patients = bookingDashboardExpertInfo?.patients || [];
  const visitsNumber = bookingDashboardExpertInfo?.visits || availableSlots.length || 0;

  useEffect(() => {
    // Lấy thông tin chuyên gia từ trạng thái location
    const expert = location.state?.expert || location.state?.selectedExpert;
    if (expert) {
      console.log('Dữ liệu chuyên gia nhận được:', expert);
      // Normalize image into a canonical `image` property so other pages/components can rely on it
      const normalizedImage = expert.avatarUrl || expert.avatar_url || expert.image || expert.avatar || null;
      const expertWithImage = { ...expert, image: normalizedImage };
      console.log('URL hình ảnh chuyên gia (normalized):', normalizedImage);
      setBookingDashboardExpertInfo(expertWithImage);
      // Lưu vào localStorage để duy trì
      localStorage.setItem('selectedExpert', JSON.stringify(expertWithImage));
    } else {
      // Thử lấy từ localStorage
      const savedExpert = localStorage.getItem('selectedExpert');
      if (savedExpert) {
        const parsedExpert = JSON.parse(savedExpert);
        console.log('Dữ liệu chuyên gia từ localStorage:', parsedExpert);
        console.log('URL hình ảnh chuyên gia từ localStorage:', parsedExpert.image);
        setBookingDashboardExpertInfo(parsedExpert);
      }
    }
  }, [location.state]);

  // Tải gói trị liệu khi chuyên gia được đặt
  useEffect(() => {
    const expert = bookingDashboardExpertInfo || (JSON.parse(localStorage.getItem('selectedExpert') || 'null'));
    if (!expert) return;

    const pkgId = expert.therapyPackageId || expert.packageId || expert.therapyPackage?.id;
    if (pkgId) {
      (async () => {
        try {
          const pkg = await therapyPackageService.getById(pkgId);
          setSelectedPackage(pkg || null);
        } catch (err) {
          console.warn('Không thể tải gói trị liệu theo id', err);
          setSelectedPackage(null);
        }
      })();
    } else {
      // lấy các gói phổ biến làm mặc định hợp lý
      (async () => {
        try {
          const list = await therapyPackageService.getPopular();
          setTherapyPackages(list || []);
          if (list && list.length) setSelectedPackage(list[0]);
        } catch (err) {
          console.warn('Không thể tải các gói phổ biến', err);
          setTherapyPackages([]);
        }
      })();
    }
  }, [bookingDashboardExpertInfo]);

  const formatVnd = (amount) => {
    if (amount === null || amount === undefined) return '—';
    try {
      // amount is decimal or number
      const n = Number(amount);
      return n.toLocaleString('vi-VN') + ' VND';
    } catch (e) {
      return amount + ' VND';
    }
  };

  const handleCalendarDateClick = (day) => {
    if (day) {
      setBookingDashboardSelectedCalendarDate(day);
      const date = new Date(bookingDashboardCurrentYear, bookingDashboardCurrentMonth - 1, day);
      // Định dạng ngày cho locale Việt Nam
      const formattedDate = date.toLocaleDateString('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      setBookingDashboardSelectedDate(formattedDate);
      // lấy lịch trống cho chuyên gia đã chọn vào ngày đã chọn (ISO yyyy-mm-dd)
      const iso = date.toISOString().split('T')[0];
      const expert = bookingDashboardExpertInfo || (JSON.parse(localStorage.getItem('selectedExpert') || 'null'));
      if (expert && expert.id) {
        (async () => {
          try {
            const resp = await appointmentService.getTherapistAvailability(expert.id, iso);
            // Backend có thể trả về mảng hoặc object với thuộc tính availability
            let slots = [];
            if (Array.isArray(resp)) {
              // mảng các chuỗi hoặc objects
              if (resp.length && typeof resp[0] === 'string') {
                slots = resp;
              } else if (resp.length && resp[0].time) {
                slots = resp.map(s => s.time);
              }
            } else if (resp && resp.availability && Array.isArray(resp.availability)) {
              slots = resp.availability.map(s => s.time);
            }

            if (slots.length) {
              setAvailableSlots(slots);
              // Không auto-select slot đầu tiên, để user tự chọn
              // setBookingDashboardSelectedTimeSlot(slots[0]);
            } else {
              setAvailableSlots([]);
            }
          } catch (err) {
            console.error('Lỗi khi tải lịch trống của chuyên gia', err);
            setAvailableSlots([]);
          }
        })();
      } else {
        // không có chuyên gia được chọn; xóa availableSlots
        setAvailableSlots([]);
      }
    }
  };

  // Phân tích các định dạng chuỗi thời gian khác nhau thành giờ/phút (24h)
  const parseTimeTo24 = (timeStr) => {
    if (!timeStr) return { hour: 0, minute: 0 };
    timeStr = timeStr.trim();
    // Nếu định dạng là HH:mm
    const hhmm = /^([0-2]?\d):([0-5]\d)$/.exec(timeStr);
    if (hhmm) {
      return { hour: parseInt(hhmm[1], 10), minute: parseInt(hhmm[2], 10) };
    }
    // Nếu định dạng là H.MM AM/PM hoặc H.MMAM
    const ampm = /^(\d{1,2})[\.:](\d{2})\s*([AP]M)?$/i.exec(timeStr);
    if (ampm) {
      let h = parseInt(ampm[1], 10);
      const m = parseInt(ampm[2], 10);
      const ampmTag = (ampm[3] || '').toUpperCase();
      if (ampmTag === 'PM' && h < 12) h += 12;
      if (ampmTag === 'AM' && h === 12) h = 0;
      return { hour: h, minute: m };
    }
    // phương án cuối: thử Date parse bằng cách nối vào hôm nay và trích xuất giờ
    const d = new Date('1970-01-01T' + timeStr);
    if (!isNaN(d.getTime())) {
      return { hour: d.getHours(), minute: d.getMinutes() };
    }
    return { hour: 0, minute: 0 };
  };

  const handlePreviousMonth = () => {
    if (bookingDashboardCurrentMonth === 1) {
      setBookingDashboardCurrentMonth(12);
      setBookingDashboardCurrentYear(bookingDashboardCurrentYear - 1);
    } else {
      setBookingDashboardCurrentMonth(bookingDashboardCurrentMonth - 1);
    }
    setBookingDashboardSelectedCalendarDate(null);
  };

  const handleNextMonth = () => {
    if (bookingDashboardCurrentMonth === 12) {
      setBookingDashboardCurrentMonth(1);
      setBookingDashboardCurrentYear(bookingDashboardCurrentYear + 1);
    } else {
      setBookingDashboardCurrentMonth(bookingDashboardCurrentMonth + 1);
    }
    setBookingDashboardSelectedCalendarDate(null);
  };

  const handleYearClick = () => {
    setBookingDashboardShowYearPicker(!bookingDashboardShowYearPicker);
  };

  const handleYearSelect = (year) => {
    setBookingDashboardCurrentYear(year);
    setBookingDashboardShowYearPicker(false);
    setBookingDashboardSelectedCalendarDate(null);
  };

  const getMonthName = (month) => {
    const months = [
      'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
      'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
    ];
    return months[month - 1];
  };

  const generateYearOptions = () => {
    const years = [];
    for (let year = 2020; year <= 2030; year++) {
      years.push(year);
    }
    return years;
  };

  const handleSetAppointment = () => {
    // Tạo cuộc hẹn qua backend - xây dựng scheduledFor và mở modal xác nhận
    if (!bookingDashboardExpertInfo) {
      setInlineMessage({ type: 'error', text: 'Vui lòng chọn chuyên gia trước khi đặt lịch' });
      return;
    }

    const currentUser = authService.getCurrentUser();
    if (!currentUser?.id) {
      // chuyển hướng đến đăng nhập nếu chưa xác thực
      setInlineMessage({ type: 'error', text: 'Vui lòng đăng nhập để đặt lịch hẹn.' });
      navigate('/login');
      return;
    }

    // Xây dựng scheduledFor cho cuộc hẹn (DateTime)
    let scheduledFor = null;
    console.log('Debug - Selected time slot:', bookingDashboardSelectedTimeSlot);
    console.log('Debug - Selected calendar date:', bookingDashboardSelectedCalendarDate);
    
    if (bookingDashboardSelectedCalendarDate && bookingDashboardSelectedTimeSlot) {
      const { hour, minute } = parseTimeTo24(bookingDashboardSelectedTimeSlot);
      console.log('Debug - Parsed time:', { hour, minute });
      // Tạo local date string với timezone Asia/Ho_Chi_Minh để tránh timezone issues
      const year = bookingDashboardCurrentYear;
      const month = String(bookingDashboardCurrentMonth).padStart(2, '0');
      const day = String(bookingDashboardSelectedCalendarDate).padStart(2, '0');
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
      const localDateTimeStr = `${year}-${month}-${day}T${timeStr}`;
      
      // Gửi local time thay vì UTC để tránh timezone confusion
      // Database sử dụng "timestamp without time zone" nên cần local time
      scheduledFor = localDateTimeStr;
      console.log('Debug - Local date string:', localDateTimeStr);
      console.log('Debug - Final scheduledFor (local time):', scheduledFor);
    } else if (bookingDashboardSelectedCalendarDate) {
      console.log('Debug - No time slot selected, using default 9:00 AM');
      const year = bookingDashboardCurrentYear;
      const month = String(bookingDashboardCurrentMonth).padStart(2, '0');
      const day = String(bookingDashboardSelectedCalendarDate).padStart(2, '0');
      const localDateTimeStr = `${year}-${month}-${day}T09:00:00`;
      
      // Gửi local time thay vì UTC
      scheduledFor = localDateTimeStr;
    } else {
      console.log('Debug - No date or time selected');
      setInlineMessage({ type: 'error', text: 'Vui lòng chọn ngày và thời gian cho cuộc hẹn' });
      return;
    }

    // Debug: Log tất cả data trước khi navigate
    console.log('Debug - Navigating to BookingConfirmation with data:', {
      expert: bookingDashboardExpertInfo,
      selectedPackage,
      selectedDate: bookingDashboardSelectedDate,
      selectedTimeSlot: bookingDashboardSelectedTimeSlot,
      scheduledFor
    });

    // Navigate trực tiếp sang trang BookingConfirmation
    navigate('/booking-confirmation', { state: {
      expert: bookingDashboardExpertInfo,
      selectedPackage,
      selectedDate: bookingDashboardSelectedDate,
      selectedTimeSlot: bookingDashboardSelectedTimeSlot,
      scheduledFor
    }});
  };

  // Function này đã được chuyển sang BookingConfirmation
  /*
  const createAppointmentWithSession = async (scheduledForParam) => {
    // ... code đã được chuyển sang BookingConfirmation
  };

  const confirmAndBook = () => {
    createAppointmentWithSession(pendingScheduledFor);
  };
  */

  // demo patients/timeSlots đã bị xóa — sử dụng `availableSlots` được tải từ backend
  const timeSlots = availableSlots.length ? availableSlots : [];

  // Tạo các ngày lịch động với useMemo để tránh re-render không cần thiết
  const calendarDays = useMemo(() => {
    // Sửa lỗi: bookingDashboardCurrentMonth bắt đầu từ 1, nhưng Date constructor cần từ 0
    const monthIndex = bookingDashboardCurrentMonth - 1; // Chuyển từ 1-12 sang 0-11
    // Để lấy số ngày trong tháng, dùng new Date(year, month, 0) với month là tháng TIẾP THEO
    const daysInMonth = new Date(bookingDashboardCurrentYear, bookingDashboardCurrentMonth, 0).getDate();
    const firstDayOfMonth = new Date(bookingDashboardCurrentYear, monthIndex, 1).getDay();
    const days = [];
    
    console.log('Debug - Calendar generation:', {
      year: bookingDashboardCurrentYear,
      month: bookingDashboardCurrentMonth,
      monthIndex,
      daysInMonth,
      firstDayOfMonth
    });
    
    // Thêm các ngày trống cho đầu tháng
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({ day: '', hasAppointment: false });
    }
    
    // Thêm các ngày thực của tháng
    for (let day = 1; day <= daysInMonth; day++) {
      // Không có cuộc hẹn demo/ngẫu nhiên - đánh dấu là không có cuộc hẹn theo mặc định
      const hasAppointment = false;
      days.push({ day, hasAppointment });
    }
    
    console.log('Debug - Generated days:', days.length, 'days');
    return days;
  }, [bookingDashboardCurrentYear, bookingDashboardCurrentMonth]);

  return (
    <div className="booking-dashboard page-with-header">
      <Header />
      
      <div className="booking-main-content">
        <div className="booking-container">
          {/* Left Column - Dashboard */}
          <div className="booking-left-column">

            {/* Visits for Today Card - Taller */}
            <div className="booking-visits-card-taller">
              <div className="visits-content">
                  <div className="visits-text">
                  <h2>Lượt thăm hôm nay</h2>
                  <div className="visits-number">{visitsNumber}</div>
                  <div className="visits-stats">
                    <div className="stat-item new-patients">
                      <div className="stat-header">
                        <span className="stat-label">Bệnh nhân mới</span>
                        <span className="stat-change positive">51% ↗</span>
                      </div>
                      <span className="stat-value">40</span>
                    </div>
                    <div className="stat-item old-patients">
                      <div className="stat-header">
                        <span className="stat-label">Bệnh nhân cũ</span>
                        <span className="stat-change negative">20% ↘</span>
                      </div>
                      <span className="stat-value">64</span>
                    </div>
                  </div>
                  
                </div>
                <div className="visits-image">
                  {/* SafeImage prevents loading from known-broken hosts and centralizes fallbacks */}
                  <div style={{ width: 160, height: 160 }}>
                    <SafeImage
                      src={getExpertImage(bookingDashboardExpertInfo)}
                      alt={bookingDashboardExpertInfo?.name || 'Doctor'}
                      className="expert-photo"
                      nameForUiAvatar={bookingDashboardExpertInfo?.name}
                      fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(bookingDashboardExpertInfo?.name || 'Expert')}&background=3b82f6&color=fff&size=400`}
                      onLoad={() => { console.log('Hình ảnh chuyên gia đã tải thành công'); }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Info - Below Visits */}
            <div className="booking-info-card">
                <div className="booking-info-header">
                  <div className="booking-icon">👨‍⚕️</div>
                  <h3>Cuộc hẹn của bạn</h3>
                </div>
                
                <div className="booking-info-content">
                  <div className="expert-info-compact">
                    <div className="expert-avatar-compact">
                      <SafeImage
                        src={getExpertImage(bookingDashboardExpertInfo)}
                        alt={bookingDashboardExpertInfo?.name || 'Default Expert'}
                        nameForUiAvatar={bookingDashboardExpertInfo?.name}
                        fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(bookingDashboardExpertInfo?.name || 'Expert')}&background=3b82f6&color=fff&size=300`}
                      />
                    </div>
                    <div className="expert-details-compact">
                      <div className="expert-name-compact">
                        {bookingDashboardExpertInfo?.name || "Chưa chọn chuyên gia"}
                      </div>
                      <div className="expert-specialty-compact">
                        {bookingDashboardExpertInfo?.specialty || "Vui lòng chọn chuyên gia"}
                      </div>
                      <div className="expert-rating-compact">
                        <span className="rating-star">⭐</span>
                        <span className="rating-value">{bookingDashboardExpertInfo?.rating || "N/A"}</span>
                      </div>
                      {!bookingDashboardExpertInfo && (
                        <div className="expert-action-compact">
                          <button 
                            className="select-expert-btn"
                            onClick={() => navigate('/book-expert')}
                          >
                            Chọn chuyên gia
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="booking-status">
                    <div className="status-badge">
                      <span className="status-dot"></span>
                      <span className="status-text">Đã xác nhận</span>
                    </div>
                  </div>
                </div>
            </div>

            {/* Patient List & Consultation - Combined */}
            <div className="booking-patient-consultation-combined">
              <div className="combined-header">
                <div className="header-left">
                  <h3>Danh sách bệnh nhân</h3>
                  <select 
                    value={bookingDashboardSelectedDate} 
                    onChange={(e) => setBookingDashboardSelectedDate(e.target.value)}
                    className="date-selector-compact"
                  >
                    <option value="Hôm nay">Hôm nay</option>
                    <option value="Ngày mai">Ngày mai</option>
                    <option value="Tuần này">Tuần này</option>
                  </select>
                </div>
                <div className="header-right">
                  <h3>Tư vấn</h3>
                </div>
              </div>
              
              <div className="combined-content">
                {/* Patient List Side */}
                <div className="patients-side">
                  <div className="patient-list-compact">
                    {(patients && patients.length) ? patients.map(patient => (
                      <div key={patient.id} className="patient-item-compact">
                        <div className="patient-avatar-compact" style={{ backgroundColor: patient.color }}>
                          {patient.avatar}
                        </div>
                        <div className="patient-info-compact">
                          <div className="patient-name-compact">{patient.name}</div>
                          <div className="patient-description-compact">{patient.description}</div>
                        </div>
                        <div className="patient-time-compact">{patient.time}</div>
                      </div>
                    )) : (
                      <div className="no-patients-placeholder">Không có bệnh nhân để hiển thị</div>
                    )}
                  </div>
                </div>
                
                {/* Consultation Side */}
                <div className="consultation-side">
                  <div className="consultation-card-compact">
                    <div className="consultation-header-compact">
                      <div className="consultation-patient-compact">
                        <div className="consultation-avatar-compact">DW</div>
                        <div className="consultation-info-compact">
                          <div className="consultation-name-compact">Denzel White</div>
                          <div className="consultation-details-compact">Male - 28 Years 3 Months</div>
                        </div>
                      </div>
                      <div className="consultation-menu-compact">⋮</div>
                    </div>
                    <div className="consultation-content-compact">
                      <div className="consultation-item-compact">
                        <span className="consultation-label-compact">Lần kiểm tra cuối:</span>
                        <span className="consultation-value-compact">
                          Dr Everly on 21 April 2021 
                          <span className="prescription-number-compact">#21983KTQ</span>
                        </span>
                      </div>
                      <div className="consultation-item-compact">
                        <span className="consultation-label-compact">Quan sát:</span>
                        <span className="consultation-value-compact">High fever and cough at normal hemoglobin levels.</span>
                      </div>
                      <div className="consultation-item-compact">
                        <span className="consultation-label-compact">Đơn thuốc:</span>
                        <div className="prescription-list-compact">
                          <div className="prescription-item-compact">Paracetmol - 2 times a day</div>
                          <div className="prescription-item-compact">Dizopam - Day and Night before meal</div>
                          <div className="prescription-item-compact">Wikoryl</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          {/* Right Column - Calendar & Booking */}
          <div className="booking-right-column">
            {/* Calendar - Unified Container */}
            <div className="calendar-unified-container">
              {/* Calendar Header */}
              <div className="booking-calendar-header">
                <div className="calendar-nav-section">
                  <button className="calendar-nav" onClick={handlePreviousMonth}>‹</button>
                  <div className="calendar-month-year">
                    <h3>{getMonthName(bookingDashboardCurrentMonth)} {bookingDashboardCurrentYear}</h3>
                    <button className="year-picker-btn" onClick={handleYearClick}>
                      {bookingDashboardShowYearPicker ? '▲' : '▼'}
                    </button>
                    {bookingDashboardShowYearPicker && (
                      <div className="year-picker-dropdown">
                        {generateYearOptions().map(year => (
                          <div 
                            key={year} 
                            className={`year-option ${year === bookingDashboardCurrentYear ? 'selected' : ''}`}
                            onClick={() => handleYearSelect(year)}
                          >
                            {year}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="calendar-nav" onClick={handleNextMonth}>›</button>
                </div>
              </div>

              {/* Calendar Weekdays - Connected */}
              <div className="booking-calendar-weekdays">
                <div className="calendar-weekdays-grid">
                  <div className="weekday">SUN</div>
                  <div className="weekday">MON</div>
                  <div className="weekday">TUE</div>
                  <div className="weekday">WED</div>
                  <div className="weekday">THU</div>
                  <div className="weekday">FRI</div>
                  <div className="weekday">SAT</div>
                </div>
              </div>

              {/* Calendar Days - Connected */}
              <div className="booking-calendar-days">
                <div className="calendar-days-grid">
                  {calendarDays.map((day, index) => (
                    <div 
                      key={index} 
                      className={`calendar-day ${day.hasAppointment ? 'has-appointment' : ''} ${bookingDashboardSelectedCalendarDate === day.day ? 'selected' : ''} ${!day.day ? 'empty-day' : ''}`}
                      onClick={() => day.day && handleCalendarDateClick(day.day)}
                    >
                      {day.day}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Available Time Slots - Without Set Appointment Button */}
            <div className="booking-time-slots">
              <div className="time-slots-header">
                <h3>Khung giờ có sẵn</h3>
                <button className="see-all-link">Xem tất cả</button>
              </div>
              <div className="time-slots-grid">
                {(availableSlots && availableSlots.length > 0 ? availableSlots : timeSlots).map((slot, index) => (
                  <button
                    key={index}
                    className={`time-slot ${bookingDashboardSelectedTimeSlot === slot ? 'selected' : ''}`}
                    onClick={() => setBookingDashboardSelectedTimeSlot(slot)}
                  >
                    {slot}
                  </button>
                ))}
              </div>
              {!bookingDashboardSelectedTimeSlot && availableSlots.length > 0 && (
                <div style={{ 
                  color: '#f97316', 
                  fontSize: '0.9rem', 
                  fontWeight: '500', 
                  textAlign: 'center', 
                  marginTop: '10px',
                  padding: '8px',
                  background: '#fef3c7',
                  borderRadius: '6px',
                  border: '1px solid #fbbf24'
                }}>
                  ⚠️ Vui lòng chọn thời gian cho cuộc hẹn
                </div>
              )}
            </div>

            {/* Quick Actions Section */}
            <div className="booking-quick-actions">
              <div className="quick-actions-header">
                <h3>Hành động nhanh</h3>
                <div className="actions-icon">⚡</div>
              </div>
              <div className="quick-actions-content">
                <div className="action-item">
                  <div className="action-icon">📝</div>
                  <div className="action-text">
                    <div className="action-title">Thêm ghi chú</div>
                    <div className="action-description">Thêm yêu cầu đặc biệt</div>
                  </div>
                </div>
                <div className="action-item">
                  <div className="action-icon">📞</div>
                  <div className="action-text">
                    <div className="action-title">Liên hệ hỗ trợ</div>
                    <div className="action-description">Nhận trợ giúp với việc đặt lịch</div>
                  </div>
                </div>
                <div className="action-item">
                  <div className="action-icon">📋</div>
                  <div className="action-text">
                    <div className="action-title">Xem lịch sử</div>
                    <div className="action-description">Các cuộc hẹn trước</div>
                  </div>
                </div>
                <div className="action-item">
                  <div className="action-icon">🔔</div>
                  <div className="action-text">
                    <div className="action-title">Đặt nhắc nhở</div>
                    <div className="action-description">Nhận thông báo trước</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Booking Summary - Full Width Below Both Columns */}
        <div className="booking-summary-full-width">
          <div className="summary-header-full-width">
            <h3>Tóm tắt đặt lịch</h3>
            <div className="order-number-integrated">
              <span className="order-text">Đơn hàng #{bookingDashboardOrderNumber.toString().padStart(3, '0')}</span>
            </div>
          </div>
          
          <div className="summary-content-full-width">
            {/* Expert Info Section */}
            <div className="summary-expert-full-width">
              <div className="expert-avatar-full-width">
                <SafeImage
                  src={getExpertImage(bookingDashboardExpertInfo)}
                  alt={bookingDashboardExpertInfo?.name || 'Default Expert'}
                  nameForUiAvatar={bookingDashboardExpertInfo?.name}
                  fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(bookingDashboardExpertInfo?.name || 'Expert')}&background=3b82f6&color=fff&size=300`}
                  onLoad={(e) => {
                    console.log('Summary expert image loaded successfully');
                    console.log('Loaded image URL:', e.target.src);
                  }}
                />
              </div>
              <div className="expert-info-full-width">
                <div className="expert-name-full-width">
                  {bookingDashboardExpertInfo?.name || "Chưa chọn chuyên gia"}
                </div>
                <div className="expert-specialty-full-width">
                  {bookingDashboardExpertInfo?.specialty || "Vui lòng chọn chuyên gia"}
                </div>
                <div className="expert-rating-full-width">
                  <div className="stat-icon rating-icon"></div>
                  {bookingDashboardExpertInfo?.rating || "N/A"}
                </div>
              </div>
            </div>
            
            {/* Date, Time & Price Section */}
            <div className="summary-details-full-width">
              {/* Package selector (if packages loaded) */}
              {((therapyPackages && therapyPackages.length) || selectedPackage) && (
                <div className="detail-item-full-width package-selector-item">
                  <div className="detail-icon-full-width">
                    <div className="package-graphic">📦</div>
                  </div>
                  <div className="detail-info-full-width">
                    <div className="detail-label-full-width">Gói</div>
                    <div className="detail-value-full-width">
                      {selectedPackage ? (
                        <div className="package-selected">
                          <div className="package-name">{selectedPackage.name || selectedPackage.packageName || selectedPackage.PackageName}</div>
                          <div className="package-price">{formatVnd(selectedPackage.totalPrice || selectedPackage.TotalPrice || selectedPackage.pricePerSession)}</div>
                        </div>
                      ) : (
                        <select className="package-select" onChange={(e) => {
                          const id = e.target.value;
                          const pkg = therapyPackages.find(p => (p.id || p.id === id || p.Id === id || p.Id) && (p.id === id || p.Id === id));
                          setSelectedPackage(pkg || null);
                        }}>
                          {therapyPackages.map(p => (
                            <option key={p.id || p.Id} value={p.id || p.Id}>{(p.name || p.packageName)} — {formatVnd(p.totalPrice || p.TotalPrice || p.pricePerSession)}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="detail-item-full-width">
                <div className="detail-icon-full-width">
                  <div className="calendar-graphic">
                    <div className="calendar-header"></div>
                    <div className="calendar-body">
                      <div className="calendar-dot"></div>
                      <div className="calendar-dot active"></div>
                    </div>
                  </div>
                </div>
                <div className="detail-info-full-width">
                  <div className="detail-label-full-width">Ngày</div>
                  <div className="detail-value-full-width">
                    {bookingDashboardSelectedCalendarDate ? 
                      `${bookingDashboardSelectedDate}${bookingDashboardSelectedTimeSlot ? `, ${bookingDashboardSelectedTimeSlot}` : ''}` : 
                      'Chọn ngày'
                    }
                  </div>
                </div>
              </div>
              
              <div className="detail-item-full-width">
                <div className="detail-icon-full-width">
                  <div className="time-graphic">
                    <div className="clock-face">
                      <div className="clock-hand hour-hand"></div>
                      <div className="clock-hand minute-hand"></div>
                      <div className="clock-center"></div>
                    </div>
                  </div>
                </div>
                <div className="detail-info-full-width">
                  <div className="detail-label-full-width">Thời gian</div>
                  <div className="detail-value-full-width">
                    {bookingDashboardSelectedTimeSlot || 'Chọn thời gian'}
                  </div>
                </div>
              </div>
              
              <div className="detail-item-full-width">
                <div className="detail-icon-full-width">
                  <div className="price-graphic">
                    <div className="coin-stack">
                      <div className="coin coin-1"></div>
                      <div className="coin coin-2"></div>
                    </div>
                  </div>
                </div>
                <div className="detail-info-full-width">
                  <div className="detail-label-full-width">Giá</div>
                  <div className="detail-value-full-width">
                    {selectedPackage ? (
                      formatVnd(selectedPackage.totalPrice || selectedPackage.TotalPrice || selectedPackage.pricePerSession)
                    ) : bookingDashboardExpertInfo?.hourlyRate || bookingDashboardExpertInfo?.price ? (
                      // hiển thị giá theo giờ của chuyên gia làm dự phòng
                      formatVnd(bookingDashboardExpertInfo.hourlyRate || bookingDashboardExpertInfo.price)
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Set Appointment Button - Below Summary */}
        <div className="set-appointment-section">
          <button
            className={`set-appointment-btn-large ${isCreatingAppointment ? 'loading disabled' : ''}`}
            onClick={handleSetAppointment}
            disabled={isCreatingAppointment}
          >
            <span className="btn-icon-large">📅</span>
            {isCreatingAppointment ? (
              <>
                <span className="btn-spinner" aria-hidden="true"></span>
                <span className="btn-text-large">Đang đặt lịch...</span>
              </>
            ) : (
              <>
                <span className="btn-text-large">Đặt lịch hẹn</span>
                <span className="btn-arrow-large">→</span>
              </>
            )}
          </button>
        </div>
        {/* Inline message / toast */}
        {inlineMessage && (
          <div className={`inline-message ${inlineMessage.type === 'error' ? 'error' : 'success'}`}>
            {inlineMessage.text}
            <button className="inline-message-close" onClick={() => setInlineMessage(null)}>✕</button>
          </div>
        )}

      </div>

      <Footer />
    </div>
  );
}